import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  Eye, Users, Heart, DollarSign, TrendingUp, 
  Calendar, Mail, FileText, ArrowUpRight, 
  ClipboardList, Wand2, MapPin, BarChart3, Store, GraduationCap
} from 'lucide-react';
import { Link } from 'react-router-dom';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Staff Quick Links - Most used tools
const staffQuickLinks = [
  {
    id: 'activity-log',
    name: 'Activity & Expense Log',
    description: 'Track ministry activities, expenses, and donations',
    icon: ClipboardList,
    path: '/admin/activity-log',
    color: 'bg-emerald-500',
    badge: 'Financial'
  },
  {
    id: 'grant-writer',
    name: 'Grant Writer',
    description: 'AI-powered grant search and application writing',
    icon: Wand2,
    path: '/admin/grants',
    color: 'bg-purple-500',
    badge: 'AI Tool'
  },
  {
    id: 'pantry-finder',
    name: 'Food Pantry Finder',
    description: 'Find and manage food pantry listings',
    icon: MapPin,
    path: '/pantry-finder',
    color: 'bg-amber-500',
    badge: 'Staff Tool',
    external: true
  },
  {
    id: 'financial-reports',
    name: 'Financial Reports',
    description: 'Monthly/quarterly income vs expense reports',
    icon: BarChart3,
    path: '/admin/activity-log',
    color: 'bg-blue-500',
    badge: 'Reports',
    hash: '#reports'
  },
  {
    id: 'businesses',
    name: 'Business Directory',
    description: 'Manage local business listings and approvals',
    icon: Store,
    path: '/admin/businesses',
    color: 'bg-orange-500',
    badge: 'Directory'
  },
  {
    id: 'business-teaching',
    name: 'Business Teaching',
    description: 'Manage student and teacher signups',
    icon: GraduationCap,
    path: '/admin/business-teaching',
    color: 'bg-teal-500',
    badge: 'Education'
  }
];

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/analytics/stats`, { withCredentials: true });
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const statCards = [
    { 
      icon: Eye, 
      label: 'Page Views (Today)', 
      value: stats?.page_views?.today || 0,
      subtext: `${stats?.page_views?.this_week || 0} this week`,
      color: 'bg-blue-500'
    },
    { 
      icon: Mail, 
      label: 'Contact Messages', 
      value: stats?.submissions?.contacts || 0,
      link: '/admin/messages',
      color: 'bg-gold'
    },
    { 
      icon: Users, 
      label: 'Volunteers', 
      value: stats?.submissions?.volunteers || 0,
      link: '/admin/volunteers',
      color: 'bg-olive'
    },
    { 
      icon: Heart, 
      label: 'Prayer Requests', 
      value: stats?.submissions?.prayers || 0,
      link: '/admin/prayers',
      color: 'bg-burgundy'
    },
    { 
      icon: DollarSign, 
      label: 'Donations (Mock)', 
      value: `$${(stats?.submissions?.donation_total || 0).toLocaleString()}`,
      subtext: `${stats?.submissions?.donations || 0} donations`,
      link: '/admin/donations',
      color: 'bg-purple'
    },
  ];

  return (
    <div data-testid="admin-dashboard">
      <div className="mb-8">
        <h1 className="font-serif text-3xl text-charcoal">Dashboard</h1>
        <p className="text-charcoal/60 mt-1">Welcome to your admin portal</p>
      </div>

      {/* Staff Quick Links - Prominent Section */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-serif text-xl text-charcoal">Staff Quick Links</h2>
          <span className="text-xs text-charcoal/50 bg-gold/10 px-3 py-1 rounded-full">
            Frequently Used Tools
          </span>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {staffQuickLinks.map((link, i) => (
            <motion.div
              key={link.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.05 }}
            >
              <Link
                to={link.path}
                className="block bg-white rounded-2xl p-5 border border-gold/10 hover:border-gold/30 hover:shadow-lg transition-all group"
                data-testid={`quick-link-${link.id}`}
              >
                <div className="flex items-start justify-between mb-3">
                  <div className={`${link.color} p-3 rounded-xl group-hover:scale-110 transition-transform`}>
                    <link.icon size={24} className="text-white" />
                  </div>
                  <span className="text-xs font-medium px-2 py-1 rounded-full bg-cream text-charcoal/60">
                    {link.badge}
                  </span>
                </div>
                <h3 className="font-semibold text-charcoal group-hover:text-burgundy transition-colors">
                  {link.name}
                </h3>
                <p className="text-sm text-charcoal/60 mt-1">{link.description}</p>
                <div className="flex items-center gap-1 mt-3 text-burgundy opacity-0 group-hover:opacity-100 transition-opacity">
                  <span className="text-sm font-medium">Open</span>
                  <ArrowUpRight size={16} />
                </div>
              </Link>
            </motion.div>
          ))}
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4 mb-8">
        {statCards.map((stat, i) => (
          <motion.div
            key={stat.label}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: i * 0.1 }}
            className="bg-white rounded-2xl p-5 border border-gold/10"
          >
            <div className="flex items-start justify-between mb-3">
              <div className={`${stat.color} p-2 rounded-lg`}>
                <stat.icon size={18} className="text-white" />
              </div>
              {stat.link && (
                <Link to={stat.link} className="text-charcoal/40 hover:text-charcoal">
                  <ArrowUpRight size={18} />
                </Link>
              )}
            </div>
            <p className="text-2xl font-semibold text-charcoal">{loading ? '...' : stat.value}</p>
            <p className="text-sm text-charcoal/60 mt-1">{stat.label}</p>
            {stat.subtext && (
              <p className="text-xs text-charcoal/40 mt-1">{stat.subtext}</p>
            )}
          </motion.div>
        ))}
      </div>

      {/* Quick Actions */}
      <div className="grid md:grid-cols-2 gap-6 mb-8">
        {/* Top Pages */}
        <div className="bg-white rounded-2xl p-6 border border-gold/10">
          <h2 className="font-serif text-lg text-charcoal mb-4">Top Pages</h2>
          {loading ? (
            <div className="animate-pulse space-y-2">
              {[1,2,3,4,5].map(i => (
                <div key={i} className="h-8 bg-cream rounded" />
              ))}
            </div>
          ) : stats?.top_pages?.length > 0 ? (
            <div className="space-y-2">
              {stats.top_pages.slice(0, 5).map((page, i) => (
                <div key={i} className="flex items-center justify-between py-2 border-b border-cream last:border-0">
                  <span className="text-sm text-charcoal/70">{page._id || 'Unknown'}</span>
                  <span className="text-sm font-medium text-charcoal">{page.count} views</span>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-charcoal/50 text-sm">No page view data yet</p>
          )}
        </div>

        {/* Quick Links */}
        <div className="bg-white rounded-2xl p-6 border border-gold/10">
          <h2 className="font-serif text-lg text-charcoal mb-4">Quick Actions</h2>
          <div className="grid grid-cols-2 gap-3">
            <Link 
              to="/admin/sermons"
              className="flex items-center gap-2 p-3 bg-cream rounded-xl hover:bg-gold/10 transition-colors"
            >
              <FileText size={18} className="text-burgundy" />
              <span className="text-sm">Add Sermon</span>
            </Link>
            <Link 
              to="/admin/events"
              className="flex items-center gap-2 p-3 bg-cream rounded-xl hover:bg-gold/10 transition-colors"
            >
              <Calendar size={18} className="text-burgundy" />
              <span className="text-sm">Add Event</span>
            </Link>
            <Link 
              to="/admin/blog"
              className="flex items-center gap-2 p-3 bg-cream rounded-xl hover:bg-gold/10 transition-colors"
            >
              <FileText size={18} className="text-burgundy" />
              <span className="text-sm">Write Blog Post</span>
            </Link>
            <Link 
              to="/admin/settings"
              className="flex items-center gap-2 p-3 bg-cream rounded-xl hover:bg-gold/10 transition-colors"
            >
              <TrendingUp size={18} className="text-burgundy" />
              <span className="text-sm">Site Settings</span>
            </Link>
          </div>
        </div>
      </div>

      {/* Recent Activity */}
      <div className="bg-white rounded-2xl p-6 border border-gold/10">
        <h2 className="font-serif text-lg text-charcoal mb-4">Recent Activity</h2>
        {loading ? (
          <div className="animate-pulse space-y-2">
            {[1,2,3,4,5].map(i => (
              <div key={i} className="h-12 bg-cream rounded" />
            ))}
          </div>
        ) : stats?.recent_activity?.length > 0 ? (
          <div className="space-y-2 max-h-[300px] overflow-y-auto">
            {stats.recent_activity.map((event, i) => (
              <div key={i} className="flex items-center justify-between py-2 px-3 bg-cream/50 rounded-lg">
                <div className="flex items-center gap-3">
                  <div className={`w-2 h-2 rounded-full ${
                    event.event_type === 'donation' ? 'bg-green-500' :
                    event.event_type === 'form_submit' ? 'bg-blue-500' :
                    'bg-charcoal/30'
                  }`} />
                  <span className="text-sm text-charcoal/70">
                    {event.event_type === 'page_view' ? `Page view: ${event.page}` :
                     event.event_type === 'form_submit' ? `Form submitted: ${event.page}` :
                     event.event_type === 'donation' ? `Donation received` :
                     event.event_type}
                  </span>
                </div>
                <span className="text-xs text-charcoal/40">
                  {new Date(event.created_at).toLocaleTimeString()}
                </span>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-charcoal/50 text-sm">No recent activity</p>
        )}
      </div>
    </div>
  );
}
